import { useState } from 'react';
import { Expense } from '../../../services/supabase/expenses.service';
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { ChevronDown, ChevronUp } from 'lucide-react';

interface ExpenseCardMobileProps {
  expense: Expense;
  onClick?: () => void;
}

export const ExpenseCardMobile = ({ expense, onClick }: ExpenseCardMobileProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getCategoryBadgeColor = (category: string) => {
    const hash = category.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const colors = ['default', 'secondary', 'outline'];
    return colors[hash % colors.length];
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const handleClick = () => {
    setIsExpanded(!isExpanded);
    onClick?.();
  };

  return (
    <Card
      className="cursor-pointer hover:bg-muted/50 transition-colors"
      onClick={handleClick}
    >
      <CardContent className="p-3">
        <div className="flex items-center justify-between gap-3">
          {/* Left: Date + Amount */}
          <div className="flex-shrink-0">
            <div className="text-xs text-muted-foreground mb-0.5">
              {formatDate(expense.expense_date)}
            </div>
            <div className="font-bold text-base">
              {expense.currency} {expense.amount.toFixed(2)}
            </div>
          </div>

          {/* Right: Category Badge + Expand Icon */}
          <div className="flex items-center gap-2">
            <Badge variant={getCategoryBadgeColor(expense.category) as any} className="text-xs">
              {expense.category}
            </Badge>
            {isExpanded ? (
              <ChevronUp className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            )}
          </div>
        </div>

        {/* Expandable Description */}
        {isExpanded && (
          <div className="mt-3 pt-3 border-t">
            <p className="text-sm font-medium">{expense.description}</p>
            {expense.notes && (
              <p className="text-xs text-muted-foreground mt-2">{expense.notes}</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
